import { createSlice, PayloadAction } from '@reduxjs/toolkit';


export interface ProjectGroupInfos {
  id: number;
  generic_name: string;
  td_group?: string;
  id_group?: number;
  name: string;
  global_score?: number;
  // Score du groupe par cycle, branches d'un même cycle combinées (calculé par l'API)
  cycle_scores?: { [cycleId: string]: { score: number; us_points: number; us_total: number } };
  members: {
    user_id: number;
    lastname: string;
    firstname: string;
    email: string;
  }[];
}

export interface E2ETest {
  name: string;
  description: string;
  status: string;
  error?: string;
}

export interface CycleResult {
  id: number;
  project_group_id: number;
  cycle_id: number;
  date: string;
  lint_errors: number;
  mypy_errors: number;
  isort_errors: number;
  git_diff_score_first_commit: number;
  git_diff_score_previous_commit: number;
  html_errors: number;
  css_errors: number;
  quality_code_score: string;
  maintainability_score: string;
  security_score?: string | null;
  us_e2e_tests: Record<string, {
    score: string;
    tests: E2ETest[];
  }>;
  user_stories_points?: number;
  global_score: number;
  // Champs enrichis pour les enseignants
  project_group?: ProjectGroupInfos;
}

// Structure typée des infos utilisateur
export interface UserInfos {
  id: number;
  login: string;
  lastname: string;
  firstname: string;
  email: string;
  access: string;
  td_group: string;
  project_group: ProjectGroupInfos;
}

export interface UserDataState {
  preferences: Record<string, any>;
  userInfos: UserInfos | null;
  // Incrémenté par « Rafraîchir Data » : les pages (Charts, résultats d'un cycle) rechargent leurs
  // données. Les résultats CI ne sont plus téléchargés en entier dans le store (refonte Charts).
  dataRefreshNonce: number;
}

const initialState: UserDataState = {
  preferences: {},
  userInfos: null,
  dataRefreshNonce: 0
};

const userDataSlice = createSlice({
  name: 'userData',
  initialState,
  reducers: {
    setPreferences: (state, action: PayloadAction<Record<string, any>>) => {
      state.preferences = action.payload;
    },
    setUserInfos: (state, action: PayloadAction<UserInfos>) => {
      state.userInfos = action.payload;
    },
    requestDataRefresh(state) {
      state.dataRefreshNonce += 1;
    }
  }
});

export const {
  setPreferences,
  setUserInfos,
  requestDataRefresh
} = userDataSlice.actions;
export const userDataReducer = userDataSlice.reducer;

// Sélecteurs
export const selectUserData = (state: any) => state.userData;
export const selectDataRefreshNonce = (state: any) => state.userData?.dataRefreshNonce ?? 0;
export const selectUserInfos = (state: any) => state.userData?.userInfos;
export const selectPreferences = (state: any) => state.userData?.preferences;
export const selectLastLogin = (state: any) => state.userData?.lastLogin;
